import 'bootstrap/dist/css/bootstrap.min.css'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, Navigate, redirect } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { ErrorBoundary } from './components/error-boundaries/error-boundary'
import { WithLayout } from './components/layouts/with-layout'
import { ErrorPage } from './error-page'
import { handleMockAuthNavigation, setMockAuthCookieUnlessSignedOut } from './utils/mock-auth-utils'
import { withLabsGate } from './utils/require-labs'
import { warnIfRuntimeConfigMissing } from './utils/runtime-env'
import { initSentry } from './utils/sentry'

import '@pixee/design-system/styles.css'

import { Root } from './root'
import type { RouteKey } from './routes'
import { Routes } from './routes'
import { AnalysisToScanRedirect } from './routes/analysis-details-page/analysis-to-scan-redirect'
import { BeaconRunPage } from './routes/beacon-run-page/beacon-run-page.tsx'
import { FreeformRunPage } from './routes/freeform-run-page/freeform-run-page.tsx'
import { LoginPage } from './routes/login-page'
import { SecurityPosturePage } from './routes/org-threat-model/security-posture-page.tsx'
import { OrganizationPage, type OrganizationPageTab } from './routes/organization-page/organization-page'
import { ProjectDetailsPage } from './routes/project-details-page/project-details-page.tsx'
import { ProjectsPage } from './routes/projects-page/projects-page.tsx'
import { RepositoriesPage } from './routes/repositories-page/repositories-page'
import { RepositoryDetailsPage } from './routes/repository-details-page/repository-details-page'
import { ScanDetailsPageWithProviders } from './routes/scan-details-page/scan-details-page'
import { ScansOverviewPage } from './routes/scans-overview-page'
import './utils/polyfills'

warnIfRuntimeConfigMissing()
initSentry()

/** @returns true when a mock auth redirect is in flight and rendering must be skipped */
async function enableMocking() {
  if (!import.meta.env.MOCKS) {
    return false
  }

  // THIS ENABLES LOGIN/LOGOUT IN THE PREVIEW PAGE — see utils/mock-auth-utils.ts.
  if (handleMockAuthNavigation()) {
    return true
  }

  setMockAuthCookieUnlessSignedOut()

  console.log('Enabling mocks')
  const worker = await Promise.all([import('msw/browser'), import('../playwright/handlers')]).then(
    ([{ setupWorker }, { defaultHandlers }]) => {
      console.log('Setting up worker')
      return setupWorker(...Object.values(defaultHandlers))
    }
  )

  console.log('Starting worker')
  await worker.start({ onUnhandledRequest: 'bypass' })
  return false
}

enableMocking().then(isMockAuthRedirectInFlight => {
  if (isMockAuthRedirectInFlight) {
    return
  }

  // eslint-disable-next-line no-restricted-properties -- app entry point, outside React tree
  const container = document.getElementById('root')
  invariant(container)
  const root = createRoot(container)

  const router = createBrowserRouter([
    {
      path: '/',
      element: <Root />,
      errorElement: <ErrorPage />,
      children: Object.keys(Routes).map(route =>
        withLabsGate(
          route as RouteKey,
          match(route as RouteKey)
            .with('LoginPage', key => ({
              path: Routes[key].path,
              element: <LoginPage />,
            }))
            .with('IntroPage', key => ({
              path: Routes[key].path,
              element: <Navigate to={Routes.RepositoriesPage.path} replace />,
            }))
            .with('WelcomePage', key => ({
              path: Routes[key].path,
              element: <Navigate to={Routes.RepositoriesPage.path} replace />,
            }))
            .with('HomePage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load repositories page">
                    <RepositoriesPage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with('RepositoriesPage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load repositories page">
                    <RepositoriesPage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with('RepositoryDetailsPage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load repository page">
                    <RepositoryDetailsPage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with(
              'AnalysisDetailsPage',
              'AnalysisDetailsPageDeprecated',
              'AnalysisDetailsPageFindingFixDrawer',
              'AnalysisDetailsPageFindingFixDrawerDeprecated',
              'AnalysisDetailsPageFindingTriageDrawer',
              key => ({
                path: Routes[key].path,
                element: (
                  <WithLayout>
                    <ErrorBoundary fallbackTitle="Unable to load analysis page">
                      <AnalysisToScanRedirect />
                    </ErrorBoundary>
                  </WithLayout>
                ),
              })
            )
            .with('ScansOverviewPage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load scans page">
                    <ScansOverviewPage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with('ScanDetailsPage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load scan details page">
                    <ScanDetailsPageWithProviders />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with('BeaconRunPage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load Beacon run page">
                    <BeaconRunPage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with('SecurityPosturePage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load security posture page">
                    <SecurityPosturePage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with('FreeformRunPage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load Findings Intake run page">
                    <FreeformRunPage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with('OrganizationPage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load organization page">
                    <OrganizationPage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with('ProjectsPage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load projects page">
                    <ProjectsPage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            .with('ProjectDetailsPage', key => ({
              path: Routes[key].path,
              element: (
                <WithLayout>
                  <ErrorBoundary fallbackTitle="Unable to load project page">
                    <ProjectDetailsPage />
                  </ErrorBoundary>
                </WithLayout>
              ),
            }))
            // The page moved from /preferences to /organization once it grew beyond preferences; keep the
            // old path working for bookmarks and shared links, carrying the tab selection over with it.
            // A bare /preferences link meant the Preferences tab, which is no longer the page's default.
            .with('OrganizationPageDeprecated', key => ({
              path: Routes[key].path,
              loader: ({ request }: { request: Request }) => {
                const params = new URL(request.url).searchParams
                if (!params.has('tab')) {
                  params.set('tab', 'preferences' satisfies OrganizationPageTab)
                }
                return redirect(`${Routes.OrganizationPage.path}?${params}`)
              },
            }))
            .exhaustive()
        )
      ),
    },
  ])

  root.render(
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
  )
})
